/* eslint-disable react/no-unknown-property */
import { Plane, Box, useHelper } from "@react-three/drei";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useControls, button, folder } from "leva";

import useThreeJsLoader, {
  initialState,
} from "../../../zustand/stores/useThreeJsLoader";
import { useShallow } from "zustand/react/shallow";
import { useFrame } from "@react-three/fiber";
import { useTranslation } from "react-i18next";
import HumanScale from "./HumanScale";
import Model from "./Model";
import Painting from "./Painting";
import CaseGenerator from "./CaseGenerator";
import AnimatedPainting from "./AnimatedPainting";

export default function ThreeJsScene() {
  // Zustand variables
  const {
    isNormalized,
    modelUrl,
    position,
    rotation,
    scale,
    animation,
    molding,
    setMolding,
    socle,
    socleColor,
    humanScale,
    setCustomPositions,
    setCustomRotations,
    setRotationAnim,
    setCustomScale,
    setSocleColor,
    toggleSocle,
    isImage,
    dataFromProps,
    toggleHumanScale,
    setStartGrid,
    setEndGrid,
  } = useThreeJsLoader(
    useShallow((state) => {
      // console.log("state =>", state);
      return {
        dataFromProps: state.dataFromProps,
        isNormalized: state.isNormalized,
        cameraInitialPosition: state.cameraInitialPosition,
        modelUrl: state.modelUrl,
        position: state.customPosition.position,
        rotation: state.customPosition.rotation,
        scale: state.customPosition.scale,
        animation: state.animation,
        molding: state.molding,
        setMolding: state.setMolding,
        socle: state.socle,
        socleColor: state.socleColor,
        humanScale: state.humanScale,
        setCustomPositions: state.setCustomPositions,
        setCustomRotations: state.setCustomRotations,
        setRotationAnim: state.setRotationAnim,
        setCustomScale: state.setCustomScale,
        setSocleColor: state.setSocleColor,
        toggleSocle: state.toggleSocle,
        toggleHumanScale: state.toggleHumanScale,
        toggleBoundingBox: state.toggleBoundingBox,
        setCameraPosition: state.setCameraPosition,
        resetCameraPosition: state.resetCameraPosition,
        setNormalize: state.setNormalize,
        isImage: state.isImage,
        setStartGrid: state.setStartGrid,
        setEndGrid: state.setEndGrid,
      };
    })
  );

  const reinitValuesFrom = dataFromProps || initialState;

  const modelOriginPivotPointRef = useRef();
  const rotateAllGroupRef = useRef();
  const saveMySocle = useRef();
  const saveMyPositions = useRef();
  const humanRef = useRef();
  const initialModelRotation = useRef();
  const groupBoxRef = useRef();
  const modelSizeRef = useRef();
  const finalSize = useRef({}); // startGrid / endGrid
  const paintingGroupRef = useRef();

  useEffect(() => {
    saveMySocle.current = socle;
  }, [socle]);

  useEffect(() => {
    saveMyPositions.current = position;
  }, [position]);

  const { t } = useTranslation();

  // Controls
  const [{ [t("leva.threeJsScene.gridHelper")]: gridHelpers }, set] =
    useControls(() => {
      return {
        [t("leva.threeJsScene.helpers")]: folder(
          {
            [t("leva.threeJsScene.gridHelper")]: {
              options: {
                [t("leva.threeJsScene.noGrid")]: "none",
                [t("leva.threeJsScene.horizontal")]: "horizontal",
                [t("leva.threeJsScene.vertical")]: "vertical",
                [t("leva.threeJsScene.both")]: "both",
              },
            },
          },
          {
            order: 6,
            collapsed: true,
          }
        ),
        [t("leva.threeJsScene.modelTransformation")]: folder(
          {
            [t("leva.threeJsScene.moveModel")]: {
              onChange: (newPos, key, e) => {
                if (!e.initial) {
                  let tmpPos = Object.values(newPos);
                  setCustomPositions(tmpPos);
                } else {
                  setCustomPositions(position);
                  set({ [t("leva.threeJsScene.moveModel")]: position });
                }
              },
              value: {
                x: position[0],
                y: position[1],
                z: position[2],
              },
              x: {
                step: 0.1,
              },
              y: {
                step: 0.1,
              },
              z: {
                step: 0.1,
              },
            },
            [t("leva.threeJsScene.rotateModel")]: {
              onChange: (newRot, key, e) => {
                if (!e.initial) {
                  let tmpRot = Object.values(newRot);
                  setCustomRotations(tmpRot);
                } else {
                  set({
                    [t("leva.threeJsScene.rotateModel")]:
                      reinitValuesFrom.customPosition.rotation,
                  });
                }
              },
              value: {
                x: rotation[0],
                y: rotation[1],
                z: rotation[2],
              },
              x: {
                min: -360,
                max: 360,
                step: 1,
              },
              y: {
                min: -360,
                max: 360,
                step: 1,
              },
              z: {
                min: -360,
                max: 360,
                step: 1,
              },
            },
            [t("leva.threeJsScene.scaleModel")]: {
              onChange: (newScale, key, e) => {
                if ((isNormalized || isImage) && !e.initial) {
                  setCustomScale(newScale);
                } else if (e.initial) {
                  setCustomScale(scale);
                  set({ [t("leva.threeJsScene.scaleModel")]: scale });
                }
              },
              value: scale,
              min: 0,
            },
          },
          {
            order: 1,
            collapsed: true,
          }
        ),
        [t("leva.threeJsScene.addSocle")]: folder(
          {
            [t("leva.threeJsScene.toggleSocle")]: button(() => {
              toggleSocle();
              if (!saveMySocle.current) {
                set({
                  [t("leva.threeJsScene.moveModel")]: [
                    saveMyPositions.current[0],
                    saveMyPositions.current[1] + 1,
                    saveMyPositions.current[2],
                  ],
                });
              } else {
                set({
                  [t("leva.threeJsScene.moveModel")]: [
                    saveMyPositions.current[0],
                    saveMyPositions.current[1] - 1,
                    saveMyPositions.current[2],
                  ],
                });
              }
            }),
            [t("leva.threeJsScene.socleColor")]: {
              onChange: (newColor, key, e) => {
                if (!e.initial) {
                  setSocleColor(newColor, newColor.a);
                } else {
                  set({
                    [t("leva.threeJsScene.socleColor")]:
                      reinitValuesFrom.socleColor,
                  });
                }
              },
              value: socleColor,
            },
          },
          {
            order: 3,
            collapsed: true,
          }
        ),
        [t("leva.threeJsScene.addRotateAnimation")]: folder(
          {
            [t("leva.threeJsScene.rotateAnimation")]: {
              onChange: (rotAnim, key, e) => {
                if (!e.initial) {
                  setRotationAnim(rotAnim);
                } else {
                  set({
                    [t("leva.threeJsScene.rotateAnimation")]:
                      reinitValuesFrom.animation,
                  });
                }
              },
              value: animation,
              min: -10,
              max: 10,
              step: 0.05,
            },
          },
          {
            order: 2,
            collapsed: true,
          }
        ),
        [t("leva.threeJsScene.addHumanScale")]: folder(
          {
            [t("leva.threeJsScene.toggleHumanScale")]: button(toggleHumanScale),
          },
          {
            order: 4,
            collapsed: true,
          }
        ),
        [t("leva.threeJsScene.addMolding")]: folder(
          {
            [t("leva.threeJsScene.moldings")]: {
              onChange: (mold, key, e) => {
                if (e.initial)
                  set({ [t("leva.threeJsScene.moldings")]: molding });
                else setMolding(mold);
              },
              disabled: !isImage,
              value: molding,
              options: {
                [t("leva.threeJsScene.none")]: "none",
                [t("leva.threeJsScene.design")]: "design",
                [t("leva.threeJsScene.ipe")]: "wood",
                [t("leva.threeJsScene.oak")]: "oak",
                [t("leva.threeJsScene.white")]: "white",
                [t("leva.threeJsScene.versailles")]: "versailles",
                [t("leva.threeJsScene.versaillesWhite")]: "versaillesblanc",
              },
            },
          },
          {
            order: 0,
            collapsed: true,
            render: () => {
              return useThreeJsLoader.getState().isImage;
            },
          }
        ),
      };
    }, [modelUrl, isNormalized, isImage]);

  useEffect(() => {
    if (humanRef.current) {
      const modelBox = new THREE.Box3().setFromObject(
        rotateAllGroupRef.current,
        true
      );
      const modelSize = modelBox.getSize(new THREE.Vector3());
      if (isNormalized) {
        humanRef.current.position.x =
          Math.round(modelSize.x || 1 / 2) +
          (position[0] > 0 ? Math.round(position[0]) : 0);
      }
    }
  }, [isNormalized, scale, position, humanRef.current]);

  // useHelper(rotateAllGroupRef, THREE.BoxHelper, "red");

  // /!\ BE CAREFUL IF YOU TOUCH THESE CALCULATIONS OR ELSE...
  useEffect(() => {
    groupBoxRef.current = new THREE.Box3().setFromObject(
      rotateAllGroupRef.current,
      false
    );
    const boxSize = groupBoxRef.current.getSize(new THREE.Vector3());
    if (modelOriginPivotPointRef.current) {
      if (boxSize.x > boxSize.z) {
        finalSize.current = {
          x: Math.ceil(boxSize.x) + Math.round(Math.abs(position[0])),
          z: Math.ceil(boxSize.x) + Math.round(Math.abs(position[2])),
        };

        modelSizeRef.current = {
          x: Math.ceil(boxSize.x),
          z: Math.ceil(boxSize.x),
        };
      } else if (boxSize.x < boxSize.z) {
        finalSize.current = {
          x: Math.ceil(boxSize.z) + Math.round(Math.abs(position[0])),
          z: Math.ceil(boxSize.z) + Math.round(Math.abs(position[2])),
        };
        modelSizeRef.current = {
          x: Math.ceil(boxSize.z),
          z: Math.ceil(boxSize.z),
        };
      } else {
        finalSize.current = {
          x: Math.ceil(boxSize.x) + Math.round(Math.abs(position[0])),
          z: Math.ceil(boxSize.z) + Math.round(Math.abs(position[2])),
        };
        modelSizeRef.current = {
          x: Math.ceil(boxSize.x),
          z: Math.ceil(boxSize.z),
        };
      }

      setStartGrid(
        finalSize.current.x % 2 === 0
          ? finalSize.current.x + 1
          : finalSize.current.x
        // finalSize.current.x
      );
      setEndGrid(
        finalSize.current.z % 2 === 0
          ? finalSize.current.z + 1
          : finalSize.current.z
        // finalSize.current.z
      );
    } else if (paintingGroupRef.current) {
      console.log("ici");

      finalSize.current = {
        x: Math.ceil(boxSize.x) + Math.round(Math.abs(position[0])),
        z: Math.ceil(boxSize.z) + Math.round(Math.abs(position[2])),
      };
      modelSizeRef.current = {
        x: Math.round(boxSize.x) === 0 ? 1 : Math.round(boxSize.x),
        z: Math.round(boxSize.z) === 0 ? 1 : Math.round(boxSize.z),
      };
      setStartGrid(
        modelSizeRef.current.z % 2 === 0
          ? modelSizeRef.current.z + 1
          : modelSizeRef.current.z
      );
      setEndGrid(
        modelSizeRef.current.x % 2 === 0
          ? modelSizeRef.current.x + 1
          : modelSizeRef.current.x
      );
    }
  }, [scale, position, isNormalized, rotation, setStartGrid, setEndGrid]);

  useFrame(() => {
    if (animation !== 0) {
      if (!initialModelRotation.current)
        initialModelRotation.current = rotation;
      rotateAllGroupRef.current.rotation.y -= animation / 250;
    }
    if (animation === 0 && initialModelRotation.current) {
      rotateAllGroupRef.current.rotation.set(0, 0, 0);
      initialModelRotation.current = null;
    }
  });

  useEffect(() => {
    if (modelOriginPivotPointRef.current)
      modelOriginPivotPointRef.current.rotation.order = "YXZ";
    if (paintingGroupRef.current)
      paintingGroupRef.current.rotation.order = "YXZ";
  }, [modelOriginPivotPointRef, paintingGroupRef]);

  return (
    <>
      <gridHelper
        rotation-x={Math.PI * 0.5}
        visible={gridHelpers === "both" || gridHelpers === "vertical"}
      />
      <gridHelper
        visible={gridHelpers === "both" || gridHelpers === "horizontal"}
      />

      <CaseGenerator modelSize={modelSizeRef.current} />

      {/* Scale the group to lower the socle (e.g. accessibility) */}
      <group scale-y={1} visible={socle}>
        {/* /!\ Do not touch Box dimension, except if you want to change socles dimensions */}
        <Box args={[0.35, 1, 0.35]} position={[0, 0.5, 0]}>
          <meshStandardMaterial
            color={`rgb(${socleColor.r}, ${socleColor.g}, ${socleColor.b})`}
            transparent
            opacity={socleColor.a}
          />
        </Box>
      </group>
      <group position={[2, 0, 0]} visible={humanScale}>
        <Plane args={[1, 1]} rotation-x={Math.PI * 0.5} ref={humanRef}>
          <meshBasicMaterial color="white" side={THREE.DoubleSide} />
          <HumanScale rotation-x={-Math.PI * 0.5} />
        </Plane>
      </group>
      <group ref={rotateAllGroupRef}>
        {modelUrl ? (
          !isImage ? (
            <group
              scale={(isNormalized && !isImage) || isImage ? scale : 1}
              ref={modelOriginPivotPointRef}
              position={isNormalized ? position : [0, 0, 0]}
              rotation={
                !isNormalized && !isImage
                  ? [0, 0, 0]
                  : rotation.map((axis) => THREE.MathUtils.degToRad(axis))
              }
            >
              <Model modelUrl={modelUrl} />
            </group>
          ) : (
            <group
              scale={isNormalized ? scale : 1}
              position={position}
              ref={paintingGroupRef}
              rotation={
                !isNormalized && !isImage
                  ? [0, 0, 0]
                  : rotation.map((axis) => THREE.MathUtils.degToRad(axis))
              }
            >
              {modelUrl.endsWith(".mp4") ? (
                <AnimatedPainting imgUrl={modelUrl} />
              ) : (
                <Painting imgUrl={modelUrl} />
              )}
            </group>
          )
        ) : null}
      </group>
    </>
  );
}
